import { Box, Container, Stack, Title, Button, Group, Modal, Text, Alert } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useApolloClient, gql } from '@apollo/client';
import GraphqlError from '@components/graphql-error';
import { GET_ITINERARY } from '@queries/itinerary';
import { GET_NARRATIVE_VERSIONS } from '@queries/narrative';
import Breadcrumb from '@components/breadcrumb';
import TitleBar from '@components/title-bar';
import ItineraryTable from '@components/itinerary-table';
import NextNav from '@components/next-nav';
import IntakeModal from '@components/itinerary-modal';
import { ItineraryItem, ItinerarySchema } from '@src/types/itinerary';
import LoadingData from '@components/graphql-loading-data';
import classes from './itinerary.module.css';
import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useChatStream } from '@hooks/useChatStream';
import ItineraryInput from '@components/itinerary-input';
import { ItineraryBuilding } from '@components/itinerary-building';
import { GET_LAST_STEP, GET_TRIP_PROFILE_DETAILS } from '@queries/trip-profiles';
import ItineraryMap from '@components/itinerary-map';
import ItineraryPdfModal from '@components/Itinerary-pdf-modal';
import { useIntegrations } from '@hooks/useIntegrations';

const EXPORT_TRAVEFY_ITINERARY = gql`
  mutation ExportTravefyItinerary($tripProfileId: ID!) {
    exportTravefyItinerary(tripProfileId: $tripProfileId) {
      success
      status
      message
      warnings
      errors
    }
  }
`;

function Itinerary() {
  const CHAT_URL = import.meta.env.CHAT_STREAM_URL || 'http://localhost:4000/stream-itinerary';
  const { clientId, tripProfileId = '' } = useParams();
  const navigate = useNavigate();
  const { hasIntegration, loading: integrationsLoading } = useIntegrations();
  const [intakeOpened, { open: intakeOpen, close: intakeClose }] = useDisclosure(false);
  const [pdfOpened, { open: pdfOpen, close: pdfClose }] = useDisclosure(false, {
    onOpen: () => console.log('Opened'),
    onClose: () => console.log('Closed'),
  });
  const { isStreaming, response, send, abort } = useChatStream(CHAT_URL);

  const {
    loading: itineraryLoading,
    error: itineraryError,
    data: itineraryQueryData,
  } = useQuery(GET_ITINERARY, {
    variables: { tripProfileId },
    skip: !tripProfileId,
  });

  // TODO: handle error
  const { data: tripProfileData } = useQuery(GET_TRIP_PROFILE_DETAILS, {
    variables: {
      id: tripProfileId,
    },
    skip: !tripProfileId,
  });

  // Drives the Narratives button label: "Generate" vs "View".
  const { data: narrativeVersionsData } = useQuery(GET_NARRATIVE_VERSIONS, {
    variables: { tripProfileId },
    skip: !tripProfileId,
    fetchPolicy: 'cache-and-network',
  });
  const hasNarratives = (narrativeVersionsData?.getNarrativeVersions?.length ?? 0) > 0;

  // Transient state only for an in-progress freshly streamed itinerary (not yet persisted or before cache update)
  const [streamItinerary, setStreamItinerary] = useState<ItineraryItem[] | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [voyHistory, setVoyHistory] = useState<string[]>([]);
  const [exportAlert, setExportAlert] = useState<{ color: 'green' | 'red'; message: string } | null>(null);
  const apollo = useApolloClient();

  const [exportTravefyMutation, { loading: exportingTravefy }] = useMutation(EXPORT_TRAVEFY_ITINERARY, {
    onCompleted: (data) => {
      const result = (data?.exportTravefyItinerary ?? null) as {
        success: boolean;
        status: string;
        message?: string | null;
        warnings?: string[] | null;
        errors?: string[] | null;
      } | null;
      if (!result) return;
      if (result.success) {
        console.log('Travefy Export:', result.message);
        setExportAlert({ color: 'green', message: result.message || 'Export succeeded.' });
      } else {
        const errMsg = result.message || (result.errors && result.errors.join('; ')) || 'Export failed';
        console.error('Failed to export Travefy itinerary:', errMsg);
        setExportAlert({ color: 'red', message: errMsg });
      }
    },
    onError: (err) => {
      console.error('Failed to export Travefy itinerary:', err);
      setExportAlert({ color: 'red', message: 'Travefy export failed' });
    },
  });

  // Existing (persisted) itinerary from GraphQL (null if none or empty array)
  const rawItinerary = itineraryQueryData?.getItinerary;

  // Memoize normalization so we don't rebuild on every keystroke
  const existingItinerary: ItineraryItem[] | null = useMemo(() => {
    if (!rawItinerary || !Array.isArray(rawItinerary) || rawItinerary.length === 0) return null;
    return rawItinerary.map((item) => ({
      day: item.day ?? 0,
      date: item.date ?? '',
      amActivity: item.amActivity ?? '',
      amAttractionId: item.amAttractionId ?? null,
      amAttractionInterest: item.amAttractionInterest ?? null,
      amAttractionUrl: item.amAttractionUrl ?? '',
      pmActivity: item.pmActivity ?? '',
      pmAttractionId: item.pmAttractionId ?? null,
      pmAttractionInterest: item.pmAttractionInterest ?? null,
      pmAttractionUrl: item.pmAttractionUrl ?? '',
      dinner: item.dinner ?? '',
      dinnerUrl: item.dinnerUrl ?? '',
      accommodations: item.accommodations ?? '',
      accommodationsUrl: item.accommodationsUrl ?? '',
      notes: item.notes ?? '',
    }));
  }, [rawItinerary]);

  // Prefer streaming version; memo for stable reference
  const effectiveItinerary: ItineraryItem[] | null = useMemo(
    () => streamItinerary || existingItinerary,
    [streamItinerary, existingItinerary]
  );

  const lastResponseRef = useRef<string | null>(null);
  const autoGenerateTriggered = useRef(false);

  // Kick off streaming when user clicks Generate; message payload kept simple for now
  const generateItinerary = useCallback(async () => {
    if (existingItinerary) return; // already have one; could allow regenerate flow if desired
    setParseError(null);
    setStreamItinerary(null);
    await send(tripProfileId, '');
  }, [existingItinerary, send, tripProfileId]);

  const sendChangeRequest = useCallback(
    async (msg: string) => {
      if (!msg.trim()) return;
      setVoyHistory((prev) => [...prev, msg.trim()]);
      await send(tripProfileId, msg.trim());
    },
    [send, tripProfileId]
  );

  // Helper to robustly parse assistantText which may be JSON or a JSON-string containing { itinerary: [...] }
  function parseItineraryFromText(text: string): ItineraryItem[] | null {
    const t = text.trim();
    const tryParse = (s: string) => {
      try {
        return JSON.parse(s);
      } catch {
        console.warn('Failed to parse itinerary JSON:', s);
        return undefined;
      }
    };
    let parsed: unknown = tryParse(t);
    if (parsed === undefined && t.startsWith('{') && t.endsWith('}')) {
      // still failed, continue below
    }
    // if first parse yields a string, try parsing again (quoted JSON case)
    if (typeof parsed === 'string') {
      const again = tryParse(parsed);
      if (again !== undefined) parsed = again;
    }
    if (!parsed || typeof parsed !== 'object') return null;
    const validated = ItinerarySchema.safeParse(parsed);
    if (validated.success) {
      return validated.data.itinerary as ItineraryItem[];
    }
    console.warn('Itinerary data failed schema validation:', validated.error);
    return null;
  }

  // When streaming completes or when an 'itinerary' event arrives (hook sets response to the payload), parse it
  useEffect(() => {
    if (!response) return;
    // Include isStreaming in the de-dup key: the same response payload must be re-processed when
    // streaming ends (isStreaming flips false) because that is when we write to the Apollo cache.
    const responseKey = `${isStreaming ? 'streaming' : 'done'}::${response}`;
    if (lastResponseRef.current === responseKey) return;
    lastResponseRef.current = responseKey;

    const result = parseItineraryFromText(response);
    if (result) {
      // Only update if different length or content changed
      const current = streamItinerary;
      const changed =
        !current || current.length !== result.length || JSON.stringify(current) !== JSON.stringify(result);

      if (changed) {
        setStreamItinerary(result);
      }
      setParseError(null);

      // When streaming completes, persist to Apollo cache regardless of whether an itinerary
      // already existed — this covers both initial generation and VoyAssist change requests.
      if (!isStreaming) {
        try {
          apollo.writeQuery({
            query: GET_ITINERARY,
            variables: { tripProfileId },
            data: { getItinerary: result },
          });
          // Drop transient state to rely on cache
          setStreamItinerary(null);
        } catch {
          console.warn('Failed to write streamed itinerary to Apollo cache.');
        }
        // Force network fetch so components depending on lastStep (e.g. Breadcrumb) update
        void apollo
          .query({
            query: GET_LAST_STEP,
            variables: { id: tripProfileId },
            fetchPolicy: 'network-only',
          })
          .catch(() => {
            console.warn('Failed to refetch lastStep after itinerary generation.');
          });
      }
    } else if (!isStreaming) {
      setParseError('Could not parse itinerary from streamed response.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response, isStreaming, apollo, tripProfileId]);

  // Auto-generate an itinerary if none exists and we're not already streaming
  useEffect(() => {
    if (autoGenerateTriggered.current) return;
    if (!tripProfileId) return;
    if (itineraryLoading) return;
    if (!existingItinerary && !streamItinerary && !isStreaming) {
      autoGenerateTriggered.current = true;
      void generateItinerary();
    }
  }, [tripProfileId, itineraryLoading, existingItinerary, streamItinerary, isStreaming, generateItinerary]);

  // return if we don't have required params
  if (!clientId) return <Text c="red">No client ID provided.</Text>;
  if (!tripProfileId) return <Text c="red">No trip profile ID provided.</Text>;

  const exportToCSV = () => {
    if (!effectiveItinerary || effectiveItinerary.length === 0) return;
    const csvRows = [];
    const headers = ['Day', 'Date', 'Weekday', 'AM Activity', 'PM Activity', 'Diner', 'Hotel'];
    csvRows.push(headers.join(','));
    effectiveItinerary.forEach((record: ItineraryItem) => {
      const row = [
        `Day ${record.day}`,
        record.date
          ? new Date(record.date)
              .toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
              .replace(/ /g, '-')
          : '',
        record.date ? new Date(record.date).toLocaleDateString('en-US', { weekday: 'long' }) : '',
        record.amActivity,
        record.pmActivity,
        record.dinner,
        record.accommodations,
      ];
      csvRows.push(row.join(','));
    });
    const csvContent = `data:text/csv;charset=utf-8,${csvRows.join('\n')}`;
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'itinerary.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToTravefy = async () => {
    if (!tripProfileId) return;
    await exportTravefyMutation({ variables: { tripProfileId } });
  };

  if (!tripProfileId) return null;
  if (isStreaming && !effectiveItinerary)
    return (
      <ItineraryBuilding
        onCancel={() => {
          abort();
        }}
      />
    );
  if (itineraryLoading && !effectiveItinerary) return <LoadingData title="Loading Itinerary..." />;
  if (itineraryError) return <GraphqlError error={itineraryError} />;

  return (
    <div>
      <Breadcrumb pb={30} pageStep={6} />
      <TitleBar mb={0} />
      <Box bg="var(--mantine-color-voyTitleBox-6)" p={10} mb={30} mt={0}>
        <Title order={2} c="white" ta="center" className={classes.title}>
          Activity Plan
        </Title>
      </Box>

      {parseError && (
        <Box p={12} mb={20} style={{ background: 'var(--mantine-color-red-1)' }}>
          <Text c="red.8">{parseError}</Text>
        </Box>
      )}

      {exportAlert && (
        <Alert color={exportAlert.color} variant="light" withCloseButton onClose={() => setExportAlert(null)} mb={20}>
          {exportAlert.message}
        </Alert>
      )}

      {effectiveItinerary && effectiveItinerary.length > 0 && (
        <Stack align="center">
          <Container fluid>
            <Group mb={20} justify="space-between">
              <Group justify="flex-start">
                <Button variant="filled" mb={10} onClick={exportToCSV}>
                  Export to CSV
                </Button>
                <ItineraryMap itinerary={effectiveItinerary} iconUrl="/pin_icon.svg" buttonLabel="Open Map" />
                <Button variant="filled" mb={10} onClick={pdfOpen}>
                  Itinerary PDF
                </Button>
                {!integrationsLoading && hasIntegration('travefy') && (
                  <Button variant="filled" mb={10} onClick={exportToTravefy} disabled={exportingTravefy}>
                    Export to Travefy
                  </Button>
                )}
              </Group>
              <Button variant="filled" mb={10} onClick={intakeOpen}>
                Trip Preferences
              </Button>
              <Button
                variant="filled"
                mb={10}
                onClick={() => navigate(`/clients/${clientId}/trip-profiles/${tripProfileId}/narratives`)}
              >
                {hasNarratives ? 'View Itineraries' : 'Generate Itinerary'}
              </Button>
            </Group>
            <ItineraryTable itinerary={effectiveItinerary} />
          </Container>
          <Modal opened={intakeOpened} onClose={intakeClose} size="97%" centered>
            <IntakeModal onClose={intakeClose} />
          </Modal>
          {/* Preview modal expects a tripProfile; we only have itinerary here, so hide it unless you provide profile */}
        </Stack>
      )}

      {existingItinerary && (
        <ItineraryInput
          isStreaming={isStreaming}
          onSend={sendChangeRequest}
          onAbort={abort}
          autoFocus
          initialValue=""
          history={voyHistory}
        />
      )}

      <NextNav saveVisible={false} done={`/clients/${clientId}`} />

      <ItineraryPdfModal
        opened={pdfOpened}
        onClose={pdfClose}
        tripProfile={tripProfileData?.singleTripProfile ?? null}
        itinerary={effectiveItinerary}
        filename={`Trip - ${tripProfileData?.singleTripProfile?.title || 'itinerary'}.pdf`}
      />
    </div>
  );
}

export default Itinerary;
